import * as React from 'react';
import { CalendarBlank, Clock, WarningCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useHolidays } from '@/hooks/useHolidays';

// 'YYYY-MM-DD' <-> local Date helpers (avoid timezone shifts from parseISO).
const toDate = (s) => {
  if (!s || typeof s !== 'string') return undefined;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};
const toStr = (dt) =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

/**
 * <DatePicker> / <TimePicker>
 * Native HTML5 date / time input with a SEPARATE icon button positioned OUTSIDE
 * the input box. Clicking the icon button calls `showPicker()` on the input,
 * which opens the browser's native calendar / time picker.
 *
 * Both behaviours are preserved:
 *  - Clicking inside the input → segment-based manual typing
 *  - Clicking the external icon button → native picker opens
 */
const baseInputClasses =
  'h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm tabular-nums ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700/20 ' +
  'focus-visible:border-teal-700 disabled:cursor-not-allowed disabled:opacity-50 ' +
  'hide-native-picker';

const baseButtonClasses =
  'h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-200 ' +
  'bg-white text-zinc-500 hover:text-teal-700 hover:bg-[#E5EDFF] hover:border-[#B3CBFF] ' +
  'transition-colors disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-zinc-500';

export const DatePicker = React.forwardRef(function DatePicker(
  { value = '', onChange, min, max, disabled, disabledDates, testId, className, title, required, id, name,
    disableNonWorking = false, warnNonWorking = false, snapForward = false },
  externalRef
) {
  const ref = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  React.useImperativeHandle(externalRef, () => ref.current);

  // 平日・祝日 対応 — hook derives the year from `value` (or current year as fallback).
  // isNonWorking/nonWorkingReason are cheap so we call them freely on renders.
  const yearFromValue = value ? parseInt(String(value).slice(0, 4), 10) : new Date().getFullYear();
  const { isNonWorking, nonWorkingReason, nextWorkingDay } = useHolidays(yearFromValue);

  const hasDisabled = Array.isArray(disabledDates) && disabledDates.length > 0;
  const isConflict = !!value && hasDisabled && disabledDates.includes(value);
  const nwReason = value && !isConflict ? nonWorkingReason(value) : null;
  // Only surface a non-working warning if the caller opted in
  const showNonWorking = !!nwReason && (disableNonWorking || warnNonWorking);
  const nonWorkingBlocks = !!nwReason && disableNonWorking;

  const disabledMatchers = React.useMemo(() => {
    const matchers = (disabledDates || []).map(toDate).filter(Boolean);
    if (min) matchers.push({ before: toDate(min) });
    if (max) matchers.push({ after: toDate(max) });
    if (disableNonWorking) matchers.push((dt) => isNonWorking(dt));
    return matchers;
  }, [disabledDates, min, max, disableNonWorking, isNonWorking]);

  const handleChange = (v) => {
    if (!v) { onChange?.(v); return; }
    if (snapForward) {
      const reason = nonWorkingReason(v);
      if (reason) {
        onChange?.(nextWorkingDay(v, 'forward'));
        return;
      }
    }
    onChange?.(v);
  };

  const conflictTitle = isConflict
    ? 'この日は既に同じ申請が登録されています'
    : (showNonWorking ? `${nwReason.name}（${nwReason.reason === 'weekend' ? '休日' : nwReason.reason === 'holiday' ? '祝日' : '会社休業日'}）` : title);

  return (
    <div className={cn('flex items-center gap-1.5 max-w-[220px]', className)}>
      <input
        ref={ref}
        id={id}
        name={name}
        type="date"
        value={value}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        required={required}
        data-testid={testId}
        data-nonworking={nwReason ? nwReason.reason : undefined}
        title={conflictTitle}
        aria-invalid={isConflict || nonWorkingBlocks || undefined}
        className={cn(
          baseInputClasses,
          'flex-1 min-w-0',
          isConflict && 'border-red-400 ring-2 ring-red-200 text-red-600',
          !isConflict && showNonWorking && (
            nonWorkingBlocks
              ? 'border-red-300 ring-1 ring-red-100 bg-red-50/40 text-red-700'
              : 'border-amber-300 ring-1 ring-amber-100 bg-amber-50/40 text-amber-800'
          )
        )}
      />
      {(
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="カレンダーを開く"
              data-testid={testId ? `${testId}-calendar` : undefined}
              className={baseButtonClasses}
            >
              <CalendarBlank size={18} weight="duotone" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={value ? toDate(value) : undefined}
              defaultMonth={value ? toDate(value) : undefined}
              disabled={disabledMatchers}
              onSelect={(d) => {
                if (d) {
                  handleChange(toStr(d));
                  setOpen(false);
                }
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
      {/* Non-working day hint / warning */}
      {showNonWorking && !isConflict && (
        <span
          data-testid={testId ? `${testId}-nonworking` : undefined}
          className={cn(
            'ml-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap',
            nonWorkingBlocks
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          )}
          title={conflictTitle || undefined}
        >
          <WarningCircle size={11} weight="fill" />
          {nwReason.reason === 'holiday' && nwReason.name}
          {nwReason.reason === 'company' && nwReason.name}
          {nwReason.reason === 'weekend' && nwReason.name}
        </span>
      )}
    </div>
  );
});

export const TimePicker = React.forwardRef(function TimePicker(
  { value = '', onChange, disabled, testId, className, title, required, id, name },
  externalRef
) {
  const ref = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  React.useImperativeHandle(externalRef, () => ref.current);

  const [selH, selM] = React.useMemo(() => {
    const m = /^(\d{1,2}):(\d{2})/.exec(value || '');
    return m ? [m[1].padStart(2, '0'), m[2]] : ['', ''];
  }, [value]);
  const hours = React.useMemo(() => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')), []);
  const minutes = React.useMemo(() => {
    const base = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
    // keep a manually-typed odd minute (e.g. :07) selectable/highlighted
    return selM && !base.includes(selM) ? [...base, selM].sort() : base;
  }, [selM]);

  const scrollToSel = (el) => {
    if (el && el.dataset.sel === '1' && el.parentElement) {
      const p = el.parentElement;
      p.scrollTop = el.offsetTop - p.clientHeight / 2 + el.clientHeight / 2;
    }
  };

  const Col = ({ items, sel, onPick, tidPrefix }) => (
    <div className="w-16 max-h-56 overflow-y-auto overscroll-contain py-1">
      {items.map((v) => (
        <button
          key={v}
          type="button"
          data-sel={v === sel ? '1' : undefined}
          ref={scrollToSel}
          onClick={() => onPick(v)}
          data-testid={testId ? `${testId}-${tidPrefix}-${v}` : undefined}
          className={cn(
            'w-full py-1.5 text-sm tabular-nums text-center rounded-md transition-colors',
            v === sel ? 'bg-teal-700 text-white font-semibold' : 'text-zinc-700 hover:bg-zinc-100'
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );

  return (
    <div className={cn('flex items-center gap-1.5 max-w-[180px]', className)}>
      <input
        ref={ref}
        id={id}
        name={name}
        type="time"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        required={required}
        data-testid={testId}
        title={title}
        className={cn(baseInputClasses, 'flex-1 min-w-0')}
      />
      {/* Custom popover picker — works everywhere (iframes, all browsers,
          touch) unlike input.showPicker(), which throws in cross-origin
          frames and is unsupported in some engines. */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="時刻を選択"
            data-testid={testId ? `${testId}-clock` : undefined}
            className={baseButtonClasses}
          >
            <Clock size={18} weight="duotone" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-2"
          align="start"
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <div className="flex items-stretch gap-1">
            <div>
              <p className="text-[10px] font-semibold text-zinc-400 text-center pb-1">時</p>
              <Col
                items={hours}
                sel={selH}
                tidPrefix="hour"
                onPick={(h) => onChange?.(`${h}:${selM || '00'}`)}
              />
            </div>
            <div className="w-px bg-zinc-100 my-1" />
            <div>
              <p className="text-[10px] font-semibold text-zinc-400 text-center pb-1">分</p>
              <Col
                items={minutes}
                sel={selM}
                tidPrefix="min"
                onPick={(m) => { onChange?.(`${selH || '00'}:${m}`); setOpen(false); }}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});
